"use client";

import { BillingPlan } from "@/components/paywall/billing-plan";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/utils";
import {
  BillingPlan as BillingPlanType,
  BillingPurchaseModes,
} from "@jurni/schemas";
import { LoaderCircle } from "lucide-react";
import { PaywallDemoFooter } from "./paywall-demo-footer";

interface PaywallPlansScreenProps {
  plans: BillingPlanType[];
  isLoading: boolean;
  selectedPlanId: number | null;
  onSubscribe: (planId: number) => void;
  onBookDemo: () => void;
}

export const PaywallPlansScreen = ({
  plans,
  isLoading,
  selectedPlanId,
  onSubscribe,
  onBookDemo,
}: PaywallPlansScreenProps) => {
  const planAlignmentClass =
    plans.length <= 2
      ? "justify-start sm:justify-center"
      : plans.length === 3
        ? "justify-start min-[1100px]:justify-center"
        : "justify-start";
  const featuredPlanId = plans.find(
    (plan) => plan.purchaseMode !== BillingPurchaseModes.BOOK_DEMO,
  )?.id;

  if (isLoading) {
    return (
      <div className="flex h-[500px] w-full items-center justify-center">
        <LoaderCircle className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div className="flex min-h-[160px] w-full flex-col justify-center gap-5 px-6 py-8 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-[520px] text-sm text-muted-foreground">
          Please try again shortly or book a demo and we&apos;ll get you set up.
        </p>
        <Button
          className="w-full flex-shrink-0 sm:w-auto"
          onClick={onBookDemo}
        >
          Book a Demo
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-col gap-3 overflow-y-auto overflow-x-hidden px-3 pb-3 pt-4">
      <div className="w-full overflow-x-auto overflow-y-hidden px-1 pb-1 pt-1">
        <div
          className={cn(
            "flex w-max min-w-full flex-row items-stretch gap-4",
            planAlignmentClass,
          )}
        >
          {plans.map((plan) => (
            <BillingPlan
              key={plan.id}
              className="w-[calc(100vw-80px)] max-w-[300px] shrink-0 sm:w-[300px]"
              plan={plan}
              onSubscribe={() => onSubscribe(plan.id)}
              onBookDemo={onBookDemo}
              isLoading={selectedPlanId === plan.id}
              isFeatured={plan.id === featuredPlanId}
            />
          ))}
        </div>
      </div>
      <div className="flex-shrink-0">
        <PaywallDemoFooter onBookDemo={onBookDemo} />
      </div>
    </div>
  );
};
